"use client";

import { usePathname } from "next/navigation";
import { type ReactElement, useEffect } from "react";
import { ConsentBanner } from "./consent-banner";
import {
  CONSENT_CATEGORIES,
  type ConsentCategory,
  type ConsentChoices,
} from "./consent-record";
import {
  activateConsentManager,
  saveConsentChoices,
  useConsentState,
} from "./consent-store";
import {
  applyGoogleConsent,
  googleCategoryStarted,
  isWebsitePath,
  silenceGoogleAnalytics,
  tagManagerLoaded,
} from "./google-tag-manager";
import {
  grantMetaPixel,
  metaPixelLoaded,
  revokeMetaPixel,
  trackMetaPageView,
} from "./meta-pixel";
import { deleteUnconsentedCookies } from "./tracking-cookies";

/** Legal pages must stay readable: banner docks there instead of blocking. */
const UNBLOCKED_PATHS: readonly string[] = ["/datenschutz", "/impressum"];

function startedInDocument(category: ConsentCategory): boolean {
  return category === "meta"
    ? metaPixelLoaded()
    : googleCategoryStarted(category);
}

/** Withdrawn category whose script already ran → reload, so its listeners and queued hits die. */
function applyChoices(
  choices: ConsentChoices,
  source: "stored" | "user",
): void {
  const withdrawn: boolean = CONSENT_CATEGORIES.some(
    (category) => !choices[category] && startedInDocument(category),
  );
  // Stored choices reached GTM in the head script already.
  if (source === "user") applyGoogleConsent(choices);
  if (choices.meta) grantMetaPixel();
  else revokeMetaPixel();
  deleteUnconsentedCookies(choices);
  if (withdrawn) window.location.reload();
}

/** Root layout mounts this on website requests only. Banner + consent wiring + Meta route PageViews. */
export function MarketingConsent(): ReactElement | null {
  const pathname: string = usePathname();
  const { active, bannerOpen, choices, source } = useConsentState();

  useEffect(() => {
    activateConsentManager();
  }, []);

  useEffect(() => {
    if (!active) return;
    if (choices === null) {
      deleteUnconsentedCookies(null);
      return;
    }
    applyChoices(choices, source);
  }, [active, choices, source]);

  // Client navigation into the app would keep tags running there: start a tag-free document.
  useEffect(() => {
    if (isWebsitePath(pathname)) return;
    if (!tagManagerLoaded() && !metaPixelLoaded()) return;
    silenceGoogleAnalytics();
    window.location.reload();
  }, [pathname]);

  useEffect(() => {
    if (choices?.meta !== true || !isWebsitePath(pathname)) return;
    trackMetaPageView(pathname);
  }, [choices, pathname]);

  if (!active || !bannerOpen || !isWebsitePath(pathname)) return null;
  return (
    <ConsentBanner
      modal={!UNBLOCKED_PATHS.includes(pathname)}
      choices={choices}
      onSave={saveConsentChoices}
    />
  );
}
